import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "../../lib/api";
import { inr, inrShort } from "../../lib/format";
import AppLayout, { PageLoader, EmptyState } from "../../components/AppLayout";
import StatCard from "../../components/ui/StatCard";
import { AllocationDonut, FlowBars, GrowthArea } from "../../components/charts";

const AUM_PERIODS = [
  ["weekly", "Weekly"],
  ["monthly", "Monthly"],
  ["quarterly", "Quarterly"],
  ["half-yearly", "Half-Yearly"],
  ["yearly", "Yearly"],
];

export default function AdminOverview() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [aumPeriod, setAumPeriod] = useState("weekly");
  const [aumRows, setAumRows] = useState(null);

  useEffect(() => {
    api.get("/admin/overview").then((r) => {
      setData(r.data);
      setAumRows(r.data.aum_growth);
    }).catch((e) => setError(apiError(e)));
  }, []);

  const changePeriod = useCallback((p) => {
    setAumPeriod(p);
    setAumRows(null);
    api.get(`/admin/overview?aum_period=${p}`)
      .then((r) => setAumRows(r.data.aum_growth))
      .catch((e) => setError(apiError(e)));
  }, []);

  if (error) return <AppLayout title="Command Center"><p className="text-red-400 text-sm">{error}</p></AppLayout>;
  if (!data) return <AppLayout title="Command Center"><PageLoader /></AppLayout>;

  const { cards, investor_growth, allocation, monthly_flows } = data;
  const aumData = (aumRows || []).map((a) => ({ label: a.label, value: a.value }));
  const invData = investor_growth.map((a) => ({
    label: new Date(a.month + "-01").toLocaleDateString("en-IN", { month: "short", year: "2-digit" }),
    value: a.count,
  }));

  return (
    <AppLayout title="Command Center" subtitle="Firm-wide assets, client activity and operational health.">
      <div className="fade-up space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="admin-overview-cards">
          <StatCard label="Total AUM" value={inrShort(cards.total_aum)} sub={inr(cards.total_aum)} testId="admin-total-aum" />
          <StatCard label="Invested Capital" value={inrShort(cards.invested_capital || 0)} sub={inr(cards.invested_capital || 0)} testId="admin-invested-capital" />
          <StatCard label="Onboarding Fee" value={inrShort(cards.onboarding_fees || 0)}
            sub={`${cards.onboarding_fee_count || 0} fees · avg ${inrShort(cards.avg_onboarding_fee || 0)}`} testId="admin-onboarding-fees" />
          <StatCard label="Interest Withdrawal" value={inrShort(cards.return_withdrawn || 0)} sub={inr(cards.return_withdrawn || 0)} testId="admin-interest-withdrawal" />
          <StatCard label="Profit Sharing" value={inrShort(cards.profit_sharing || 0)} sub={inr(cards.profit_sharing || 0)} testId="admin-profit-sharing" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-count-cards">
          <StatCard label="Total Investors" value={cards.total_investors} testId="admin-total-investors" />
          <StatCard label="Active Investors" value={cards.active_investors} testId="admin-active-investors" />
          <StatCard label="Active WRMs" value={cards.active_wrms} testId="admin-active-wrms" />
          <StatCard label="Exit Fees" value={inrShort(cards.exit_fees || 0)} sub={inr(cards.exit_fees || 0)} testId="admin-exit-fees" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3 glass-card p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="font-heading font-semibold text-white">AUM Growth</h3>
                <span className="text-[11px] text-zinc-500">Aggregate client assets · recorded valuations only</span>
              </div>
              <div className="flex gap-1.5" data-testid="admin-aum-period-selector">
                {AUM_PERIODS.map(([key, label]) => (
                  <button key={key} data-testid={`admin-aum-period-${key}`} onClick={() => changePeriod(key)}
                    className={`text-[11px] font-semibold rounded-full px-3 py-1.5 border transition-colors duration-150 ${
                      aumPeriod === key ? "pill-active" : "border-white/10 text-zinc-500 hover:text-zinc-200"
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {aumRows === null ? <div className="skeleton h-[300px]" data-testid="admin-aum-loading" /> :
              aumData.length === 0 ? <EmptyState message="No valuation history recorded for this period yet." /> : (
                <GrowthArea data={aumData} connectNulls={false} testId="admin-aum-growth-chart" />
              )}
          </div>
          <div className="lg:col-span-2 glass-card p-6">
            <h3 className="font-heading font-semibold text-white mb-4">Firm Asset Allocation</h3>
            <AllocationDonut data={allocation} testId="admin-allocation-chart" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="glass-card p-6">
            <h3 className="font-heading font-semibold text-white mb-4">Investor Growth</h3>
            <GrowthArea data={invData} height={240} testId="admin-investor-growth-chart" />
          </div>
          <div className="glass-card p-6">
            <h3 className="font-heading font-semibold text-white mb-4">Monthly Inflows vs Outflows</h3>
            <FlowBars data={monthly_flows} height={240} testId="admin-flows-chart" />
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
